/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { reaction } from 'mobx';
import * as React from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useForceRender } from 'src/core/hooks/useForceRender';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { noopUndefined } from 'src/core/utils/base';
import { getLifetime } from 'src/core/utils/lifetime';

/**
 * A hook responsible for creating new progress trackers.
 * **Use** `useProgressTracker` instead of this, if you don't want to
 * create trackers dynamically.
 */
export const useImperativeProgressTracker = () => {
    const lifetimeRef = React.useRef(getLifetime());
    const forceRender = useForceRender();

    React.useEffect(() => lifetimeRef.current.dispose, []);

    /**
     * Creates and returns new ProgressTracker instance and
     * attaches onRunning and onComplete functions to be called
     * when the tracker.state changes.
     */
    const createTracker = React.useCallback(
        <T>({ onDone, onRunning = noopUndefined }: { onRunning?: () => void; onDone: (val?: T) => void }) => {
            const tracker = new ProgressTrackerStore<T>();
            lifetimeRef.current.add(
                reaction(
                    () => tracker.state,
                    state => {
                        unstable_batchedUpdates(() => {
                            if (state === 'running') {
                                onRunning();
                            } else {
                                onDone(tracker.context);
                            }
                            forceRender();
                        });
                    }
                )
            );

            return tracker;
        },
        [forceRender]
    );

    return { createTracker };
};
